import '../svg-icon/svg-icon';
import '../input/button-input'

import { modulo } from '../../_utils/modulo';
import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DragGesture, FullGestureState } from '@use-gesture/vanilla';
import { trapFocus, releaseFocus } from '../../_utils/trap-focus';

export interface Asset {
    title: string;
    alt: string;
    caption: string;
    src: string;
    width: number;
    height: number;
}

@customElement('lightbox-element')
export class LightboxElement extends LitElement {

    // Props

    @property({ type: Array })
    assets: Asset[] = [];

    @property({ type: Number })
    activeIndex: number = 0;

	@state()
    _imageLoaded: boolean = true;

	@query('.Lightbox-navOverlay')
	navOverlay!: HTMLElement;

	_dragGesture: DragGesture|null = null;
	_dragIsActive: boolean = false;
	_dragX: number = 0;

    // Lifecycle Hooks

    firstUpdated() {
        this.classList.add('Lightbox');
		window.addEventListener('keydown', this._handleKeyDown.bind(this));
		this._dragGesture = new DragGesture(this.navOverlay, this._handleDrag.bind(this));
    }

	disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('keydown', this._handleKeyDown.bind(this));
    }

    // render
    
    render() {
        const activeAsset = this.assets[this.activeIndex];
        return html`
            <nav class="Lightbox-nav">
				<div class="Lightbox-navOverlay">
					<div 
						class="Lightbox-navArea Lightbox-navArea--prev"
						@click="${this._prev}">
					</div>
					<div 
						class="Lightbox-navArea Lightbox-navArea--next"
						@click="${this._next}">
					</div>
				</div>
                <button-input
                    type="action"
                    name="lightbox"
                    @click="${this._prev}">
                    <div slot="label">
                        ←
                    </div>
                </button-input>
                <button-input
                    type="action"
                    name="lightbox"
                    @click="${this._next}">
                    <div slot="label">
                        →
                    </div>
                </button-input>
                <button-input
                    type="action"
                    name="lightbox"
                    @click="${ () => { this.close() } }">
                    <div slot="label">
                        <svg-icon name="close"></svg-icon>
                    </div>
                </button-input>
            </nav>
            <figure class="Lightbox-figure">
                <picture 
                    class="Picture ${ this._imageLoaded ? '' : 'is-loading' }"
                    style="aspect-ratio: ${ activeAsset.width / activeAsset.height }">
                    <img 
                        src="${ activeAsset.src }"
                        title="${ activeAsset.title }"
                        alt="${ activeAsset.alt }"
						@load="${ this._handleLoad }"
                    />
                </picture>
                <figcaption 
                    class="Lightbox-caption">
                    ${ unsafeHTML(activeAsset.caption) }
                </figcaption>
            </figure>
        `;
    }

    createRenderRoot() {
        return this;
    }

    // methods
	
	_handleKeyDown(e: KeyboardEvent) {
		switch (e.key) {
            case 'ArrowLeft':
				this._prev();
                break;
			case 'ArrowRight':
                this._next();
                break;
			case 'Escape':
                this.close();
                break;
        }
	}

	_handleDrag({ active, first, movement: [mx] }: FullGestureState<"drag">) {
		if (first) {
			this._dragX = 0;
			this._dragIsActive = true;
		}
		if (active && this._dragIsActive) {
			if (mx > 50) {
				this._next();
				this._dragIsActive = false;
			}
			if (mx < -50) {
				this._prev();
				this._dragIsActive = false;
			}
		}
		if (!active) {
			this._dragX = 0;
			this._dragIsActive = false;
		}
	}

	_handleLoad() {
		setTimeout(() => {
			this._imageLoaded = true;
		});
	}

	_preloadAdjacentImages() {
        const prevIndex = modulo(this.activeIndex - 1, this.assets.length);
        const nextIndex = modulo(this.activeIndex + 1, this.assets.length);
        [prevIndex, nextIndex].forEach(index => {
            const img = new Image();
            img.src = this.assets[index].src;
        });
    }

	_prev() {
		this._imageLoaded = false;
		this.activeIndex = modulo(this.activeIndex - 1, this.assets.length);
		this._preloadAdjacentImages();
	}

	_next() {
		this._imageLoaded = false;
		this.activeIndex = modulo(this.activeIndex + 1, this.assets.length);
		this._preloadAdjacentImages();
	}

    close() {
        this.classList.remove('is-open');
		document.documentElement.style.removeProperty('overflow');
		releaseFocus();
    }

    open() {
        this.classList.add('is-open');
		document.documentElement.style.overflow = 'hidden';
		trapFocus(this);
    }

}